<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-filter" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Filter Enrollees</h4>
              <small style="opacity: .8;">Advanced search and export</small>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- Filter Card -->
        <div class="card table-card shadow mb-4">
          <div class="form-card-header">
            <div class="section-number"><i class="fe fe-sliders" style="font-size:13px;"></i></div>
            <div>
              <p class="mb-0 text-white font-weight-bold" style="font-size:.95rem;">Search Parameters</p>
              <small style="color:rgba(255,255,255,.7);">Apply filters to narrow down enrollee records</small>
            </div>
          </div>
          <div class="card-body">
            <div class="row">

              <div class="form-group col-md-12">
                <label class="info-label d-block mb-1">Program</label>
                <v-select v-model="form.sectors" multiple :options="sectors"></v-select>
              </div>

              <div class="form-group col-md-12">
                <label class="info-label d-block mb-1"><i class="fe fe-briefcase mr-1"></i>HMO</label>
                <v-select v-model="form.hmo" :options="tpas" label="organization_name"></v-select>
              </div>

              <div class="form-group col-md-12">
                <label class="info-label d-block mb-1"><i class="fe fe-briefcase mr-1"></i>{{ label.place_of_work_label }}s</label>
                <v-select v-model="form.place_of_work" :options="mdas" label="name" :reduce="(name) => name.name"></v-select>
              </div>

              <div class="form-group col-md-4">
                <label class="info-label d-block mb-1">Select LGA</label>
                <select class="form-control" v-model="form.localgovt" @change="fetchWards($event)">
                  <option v-for="lga in lga_states" v-bind:key="lga" :value="lga">{{ lga.local_name }}</option>
                </select>
              </div>

              <div class="form-group col-md-4">
                <label class="info-label d-block mb-1">Vulnerable Group</label>
                <select class="form-control" v-model="form.category_of_vulnerable_group">
                  <option value="">All</option>
                  <option :value="vul.name" v-for="vul in vulnerables" :key="vul.name">{{ vul.name }}</option>
                </select>
              </div>

              <div class="form-group col-md-4">
                <label class="info-label d-block mb-1">Gender</label>
                <select class="form-control" v-model="form.gender">
                  <option v-for="gender in genders" v-bind:key="gender" :value="gender">{{ gender }}</option>
                </select>
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Health Facility</label>
                <select class="form-control" v-model="form.provider_id">
                  <option v-for="provider in providers" v-bind:key="provider" :value="provider.id">{{ provider.agency_name }}</option>
                </select>
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Provider LGA</label>
                <select class="form-control" v-model="form.provider_localgovt">
                  <option value="">All</option>
                  <option v-for="lga in lga_states" v-bind:key="lga.id" :value="lga.id">{{ lga.local_name }}</option>
                </select>
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Ward</label>
                <select class="form-control" v-model="form.ward">
                  <option v-for="ward in wards" v-bind:key="ward.id" :value="ward">{{ ward.ward_name }}</option>
                </select>
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1"><i class="fe fe-calendar mr-1"></i>Start Date</label>
                <input type="date" class="form-control" v-model="form.from" />
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1"><i class="fe fe-calendar mr-1"></i>End Date</label>
                <input type="date" class="form-control" v-model="form.to" />
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Age</label>
                <input type="number" class="form-control" v-model="form.dob" />
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Age Operator</label>
                <v-select v-model="form.operator" :options="operators" label="name" :reduce="(name) => name.value"></v-select>
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Order By</label>
                <select class="form-control" v-model="form.order_by">
                  <option v-for="order in orders" :key="order" :value="order.value">{{ order.name }}</option>
                </select>
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Excel Group By</label>
                <select class="form-control" v-model="form.group_by">
                  <option value="">None</option>
                  <option v-for="group in groupOptions" :key="group.value" :value="group.value">{{ group.name }}</option>
                </select>
              </div>

              <div class="form-group col-md-3">
                <label class="info-label d-block mb-1">Format</label>
                <select class="form-control" v-model="form.format">
                  <option value="">JSON (Default)</option>
                  <option value="excel">Excel</option>
                </select>
              </div>

              <div class="form-group col-md-3" v-if="form.format !== 'excel'">
                <label class="info-label d-block mb-1">Principal Rows</label>
                <select class="form-control" v-model="form.per_page">
                  <option value="100">100</option>
                  <option value="500">500</option>
                  <option value="1000">1,000</option>
                  <option value="2000">2,000</option>
                  <option value="5000">5,000</option>
                  <option value="10000">10,000</option>
                  <option value="20000">20,000</option>
                  <option value="30000">30,000</option>
                  <option value="40000">40,000</option>
                  <option value="50000">50,000</option>
                  <option value="100000">100,000</option>
                  <option value="200000">200,000</option>
                  <option value="300000">300,000</option>
                </select>
              </div>

              <div class="col-md-6 mb-3">
                <div class="d-flex align-items-center" style="gap:8px;">
                  <input type="checkbox" v-model="form.dependents" style="width:18px;height:18px;" />
                  <label class="mb-0" style="font-size:.88rem;">Filter with dependents</label>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="d-flex align-items-center" style="gap:8px;">
                  <input type="checkbox" v-model="inspect" style="width:18px;height:18px;" />
                  <label class="mb-0" style="font-size:.88rem;">See excluded {{ label.place_of_work_label }}s</label>
                </div>
              </div>

              <div class="form-group col-md-12 mb-3" v-show="inspect">
                <label class="info-label d-block mb-1">
                  <i class="fe fe-briefcase mr-1"></i>Excluded {{ label.place_of_work_label }}s
                  <button class="btn btn-sm btn-outline-secondary ml-2" @click="getDefaultMDAs">Default</button>
                  <button class="btn btn-sm btn-outline-secondary ml-1" @click="clearMDAs"><i class="fe fe-x"></i></button>
                </label>
                <v-select v-model="form.establishments" multiple :options="mdas" label="name" :reduce="(name) => name.name"></v-select>
              </div>

              <div class="col-md-12 pt-2 border-top">
                <div class="d-flex flex-wrap" style="gap:10px;">
                  <button @click="filterEnrollees()" class="btn btn-submit px-4" :disabled="disabled">
                    <i class="fe fe-filter mr-1"></i> Filter Enrollees
                  </button>
                  <button @click="overAgedDeps()" class="btn btn-outline-secondary">
                    <i class="fe fe-alert-circle mr-1"></i> Over Aged Dependents
                  </button>
                </div>
                <SavedReports type="enrollees" :get-filters="savedFilters" @apply="applySaved" />
                <p class="text-primary mt-2 mb-0" v-if="disabled" style="font-size:.85rem;">
                  <i class="fe fe-loader mr-1"></i> Preparing your document…
                </p>
              </div>

            </div>
          </div>
        </div>

        <!-- Download Result -->
        <div v-show="showdownload" class="card table-card shadow mb-4">
          <div class="card-body d-flex align-items-center justify-content-between flex-wrap" style="gap:12px;">
            <div>
              <p class="mb-1 font-weight-bold">
                Total Records: <span class="text-primary">{{ results.meta.total }}</span>
              </p>
              <p class="mb-0 text-muted" style="font-size:.82rem;">
                Filtered: {{ results.data.length }}
                <strong class="ml-1">(Page {{ current_page }} of {{ results.meta.last_page }})</strong>
              </p>
            </div>
            <button class="btn btn-submit" @click="downloadGroupedExcel" :disabled="disabled">
              <i class="fe fe-download mr-1"></i> Download Grouped Excel
            </button>
          </div>
        </div>

        <!-- Preview Table -->
        <div v-show="showdownload" class="card table-card shadow mb-4">
          <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
            <div class="d-flex align-items-center">
              <i class="fe fe-list text-primary mr-2"></i>
              <h6 class="mb-0 font-weight-bold">Preview Data</h6>
            </div>
            <button class="btn btn-sm btn-outline-secondary" @click="inspectResults = !inspectResults">
              {{ inspectResults ? 'Hide' : 'Show' }}
            </button>
          </div>
          <div class="table-responsive" v-show="inspectResults">
            <table class="table align-td-middle mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">NO.</th>
                  <th class="table-th">Account Type</th>
                  <th class="table-th">Name (Agency Number)</th>
                  <th class="table-th">Phone Number</th>
                  <th class="table-th">Sector</th>
                  <th class="table-th">Facility</th>
                  <th class="table-th">Date Enrolled</th>
                  <th class="table-th">DOB/ Expiry Date</th>
                  <th class="table-th" v-if="user && user.id == '95930'"></th>
                </tr>
              </thead>
              <tbody>
                <tr class="table-row" v-for="(client, index) in results.data" v-bind:key="client.id">
                  <td>{{ index + 1 }}</td>
                  <td>{{ client.account_type }}</td>
                  <td>
                    <router-link :to="{ path: '/client/' + client.id }">
                      {{ client.firstname }} {{ client.lastname }}
                    </router-link>
                    <b> ({{ client.id_card_number }}) </b>
                  </td>
                  <td>{{ client.phone_number }}</td>
                  <td>{{ client.sector }}</td>
                  <td>
                    <span v-if="client.userprovider != null">{{ client.userprovider.agency_name }}</span>
                  </td>
                  <td>{{ client.created_at }}</td>
                  <td>{{ client.dob }}|| {{ client.expiry_date }}</td>
                  <td v-if="user && user.id == '95930'">
                    <button class="btn btn-sm btn-outline-secondary" @click="updateExp(client)">
                      update Exp {{ client.id }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </main>

    <div class="vld-parent">
      <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
    </div>
  </section>
</template>

<script>
  // Import component
  import Loading from "vue-loading-overlay";
  import SavedReports from "@/views/reports/components/SavedReports.vue";
  // Import stylesheet
  import "vue-loading-overlay/dist/vue-loading.css";
  // Init plugin
  import Navbar from "@/views/Navbar.vue";

  import moment from "moment";
  moment().format();
  import sectorsJson from "@/json/sectors.json";
  import { downloadGroupedXlsx } from "@/service/xlsx_export";

  let agency = JSON.parse(localStorage.getItem("agency"));
  let settings = agency && agency.settings ? JSON.parse(agency.settings) : null;

  moment().format();

  export default {
    components: { SavedReports,
      Loading,
      Navbar,
    },
    data() {
      return {
        agency: agency,
        settings: settings,
        current_page: "",
        header: ["KGSHIA"],

        label: {
          place_of_work_label: "MDA",
        },

        genders: ["Male", "Female"],
        dep_genders: ["Male", "Female"],
        operators: [
          {
            name: "Less than",
            value: "<",
          },
          {
            name: "Greater than",
            value: ">",
          },
          {
            name: "Equal to",
            value: "=",
          },
        ],

        sectors: sectorsJson.filter((s) => s),
        vulnerables: [
          { name: "Pregnant Women" },
          { name: "Children under 5" },
          { name: "Aged" },
          { name: "People with Special Needs" },
          { name: "Poorest of the Poor" },
          { name: "State Pensioners" },
          { name: "LGA Pensioners" },
        ],

        orders: [
          {
            name: "Provider",
            value: "provider_id",
          },
          {
            name: "HMO",
            value: "org_id",
          },
        ],

        groupOptions: [
          {
            name: "HMO",
            value: "hmo",
          },
          {
            name: "Provider",
            value: "provider",
          },
          {
            name: "Place of Work",
            value: "place_of_work",
          },
        ],

        loader: true,
        showdownload: false,
        inspect: false,
        inspectResults: false,
        user: null,
        date: null,
        form: {
          gender: "",
          dependents: true,
          category_of_vulnerable_group: "",
          hmo: "",
          hmos: [],
          establishments: [],
          sectors: ["Voluntary Contributor", "State Civil Servant", "T-SHIP"],
          ward: "",
          localgovt: "",
          place_of_work: "",
          provider_id: "",
          provider_localgovt: "",
          org_id: "",
          from: "",
          to: "",
          dob_operator: "",
          dob: "",
          dep_operator: "",
          dep_dob: "",
          dep_gender: "",
          per_page: 300000,
          current_page: 1,
          order_by: "",
          group_by: "",
          format: "excel",
        },

        edit: false,
        show: false,
        disabled: false,
        isLoading: false,
        fullPage: false,
        agency_id: "",
        providers: "",
        tpas: "",
        mdas: [],
        state: "",
        lga_states: "",
        wards: [],

        results: {
          meta: {
            total: 0,
          },
          data: [],
        },

        json_data: [],
        json_meta: [
          [
            {
              key: "charset",
              value: "utf-8",
            },
          ],
        ],
      };
    },
    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/lga/2669`)
        .then((response) => {
          this.lga_states = response.data.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    computed: {
      addOneDay() {
        var result = new Date(this.to);
        result.setDate(result.getDate() + 1);
        return result;
      },

      sortedItems() {
        // Create a shallow copy to avoid mutating the original array
        return this.results.data.slice().sort((a, b) => {
          const nameA = (a.userprovider && a.userprovider.agency_name) || '';
          const nameB = (b.userprovider && b.userprovider.agency_name) || '';
          // Case-insensitive comparison
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });
      },
    },
    methods: {
      // Saved filters: the request payload (used by scheduled exports) plus the form to restore.
      savedFilters() {
        return { ...this.buildFilterPayload(), _form: JSON.parse(JSON.stringify(this.form)) };
      },
      applySaved(filters) {
        if (filters && filters._form) {
          this.form = Object.assign({}, this.form, filters._form, { current_page: 1 });
        }
      },
      buildFilterPayload(extra = {}) {
        return {
          agency_id: 90,
          sectors: this.form.sectors,
          hmo: this.form.hmo == null || this.form.hmo === "" ? null : this.form.hmo.id,
          establishments: this.form.establishments,
          gender: this.form.gender,
          category_of_vulnerable_group: this.form.category_of_vulnerable_group,
          from: this.form.from,
          to: this.form.to,
          dob_operator: this.form.dob_operator,
          dob:
            this.form.dob == ""
              ? this.form.dob
              : moment().subtract(this.form.dob, "years").format("YYYY-MM-DD"),
          dependents: this.form.dependents,
          provider_id: this.form.provider_id,
          place_of_work: this.form.place_of_work,
          org_id: this.form.org_id,
          lga_id: this.form.localgovt ? this.form.localgovt.id : null,
          ward: this.form.ward ? this.form.ward.id : null,
          dep_dob:
            this.form.dep_dob == ""
              ? this.form.dep_dob
              : moment().subtract(this.form.dep_dob, "years").format("YYYY-MM-DD"),
          dep_operator: this.form.dep_operator,
          dep_gender: this.form.dep_gender,
          per_page: this.form.per_page,
          page: this.form.current_page,
          order_by: this.form.order_by,
          group_by: this.form.group_by,
          format: this.form.format,
          ...extra,
        };
      },

      filterEnrollees() {
        if (this.form.format === "excel") {
          this.downloadExcelFromServer();
          return;
        }

        this.disabled = true;
        this.isLoading = true;
        this.axios
          .post(`/api/v1/auth/filtersectordashboardwardlgabydate`, this.buildFilterPayload())
          .then((response) => {
            this.results = response.data;
            console.log(response);
            this.disabled = false;
            this.showdownload = true;
            this.isLoading = false;
            this.header = [
              "KGSHIA",
              'Prepared By: ' + (this.user ? this.user.email : ""),
              'Date Prepared: ' + moment().format("YYYY-MM-DD"),
              'HMO: ' + (this.form.hmo ? this.form.hmo.organization_name : 'All')
            ];
            this.$toasted.success("Filtered Successfully", {
              position: "top-center",
              duration: 3000,
            });
          })
          .catch((error) => {
            this.disabled = false;
            this.isLoading = false;
            this.$toasted.error("Error!", {
              position: "top-center",
              duration: 3000,
            });

            console.error(error);
          });
      },

      // The server builds the (grouped) workbook and streams it; the browser only saves the file.
      async downloadExcelFromServer() {
        this.disabled = true;
        this.isLoading = true;
        try {
          const response = await this.axios.post(
            `/api/v1/auth/filtersectordashboardwardlgabydate`,
            this.buildFilterPayload({ excel_meta: this.buildExcelMeta() }),
            { responseType: "blob", timeout: 0 }
          );
          const disposition = response.headers["content-disposition"] || "";
          const matched = disposition.match(/filename="?([^";]+)"?/i);
          const fileName = matched && matched[1]
            ? matched[1]
            : `filtered_enrollees_${this.form.group_by || "all"}_${moment().format("YYYY-MM-DD")}.xlsx`;

          const url = window.URL.createObjectURL(new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }));
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.$toasted.success("Excel file downloaded successfully", { position: "top-center", duration: 3000 });
        } catch (error) {
          console.error(error);
          let message = "Error generating the Excel file";
          // With responseType "blob", error bodies arrive as a Blob too.
          if (error.response && error.response.data instanceof Blob) {
            try {
              const body = JSON.parse(await error.response.data.text());
              message = body.message || message;
            } catch (e) { /* not JSON */ }
          }
          this.$toasted.error(message, { position: "top-center", duration: 4000 });
        } finally {
          this.disabled = false;
          this.isLoading = false;
        }
      },

      downloadGroupedExcel() {
        if (!this.results || !Array.isArray(this.results.data) || this.results.data.length === 0) {
          this.$toasted.error("No records to export", {
            position: "top-center",
            duration: 3000,
          });
          return;
        }

        const fileName = `filtered_enrollees_${this.form.group_by || "all"}_${moment().format("YYYY-MM-DD")}.xlsx`;
        downloadGroupedXlsx(this.results, this.form.group_by, fileName, this.buildExcelMeta());

        this.$toasted.success("Excel file downloaded successfully", {
          position: "top-center",
          duration: 3000,
        });
      },

      overAgedDeps() {
        this.isLoading = true;

        this.axios
          .post(`/api/v1/auth/overAgedDependents`, {
            agency_id: 90,
            per_page: this.form.per_page,
          })
          .then((response) => {
            this.results = response.data;
            console.log(response);
            this.$toasted.success("Dependents Queried Successfully", {
              position: "top-center",
              duration: 3000,
            });
            this.isLoading = false;
            this.showdownload = true;
          })
          .catch((error) => {
            console.error(error);
            this.isLoading = false;
          });
      },
      fetchLga() {
        this.axios
          .get(`/api/v1/auth/lga/2669`)
          .then((response) => {
            this.lga_states = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },
      updateExp(client) {
        this.axios
          .post(`/api/v1/auth/dep/expiry`, {
            dep_id: client.id,
          })
          .then((response) => {
            console.log(response);
            this.$toasted.success("updated Successfully", {
              position: "top-center",
              duration: 3000,
            });
          })
          .catch((error) => {
            console.error(error);
          });
      },
      fetchWards() {
        this.axios
          .get(`/api/v1/auth/getwards/${this.form.localgovt.id}`)
          .then((response) => {
            this.wards = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },
      getDefaultHMOs() {
        this.form.hmos = settings && settings.default_hmos ? settings.default_hmos : [];
      },
      clearHMOs() {
        this.form.hmos = [];
      },

      getProviders() {
        this.axios
          .get(`/api/v1/auth/providerAgency/90`)
          .then((response) => {
            this.providers = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },
      getMDAs() {
        this.axios
          .get(`/api/v1/auth/ministries/90`)
          .then((response) => {
            this.mdas = response.data.data;
          })
          .catch(() => { });
      },

      getTPAs() {
        this.user = JSON.parse(localStorage.getItem("user"));

        this.axios
          .get(`/api/v1/auth/org_agency/90`)
          .then((response) => {
            this.tpas = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },
      getDefaultMDAs() {
        this.form.establishments = settings && settings.excluded_mdas ? settings.excluded_mdas : [];
      },
      clearMDAs() {
        this.form.establishments = [];
      },

      buildExcelMeta() {
        const user  = JSON.parse(localStorage.getItem("user")) || {};
        const uName = user.name
          || [user.firstname, user.lastname].filter(Boolean).join(" ")
          || user.email
          || "Unknown";

        const providerObj = Array.isArray(this.providers) && this.form.provider_id
          ? this.providers.find((p) => String(p.id) === String(this.form.provider_id))
          : null;

        return {
          agencyId: 90,
          generatedBy: uName,
          generatedAt: moment().format("YYYY-MM-DD HH:mm:ss"),
          filters: {
            "Agency ID": 90,
            Program:             Array.isArray(this.form.sectors) && this.form.sectors.length
                                   ? this.form.sectors.join(", ")
                                   : "All",
            HMO:                 this.form.hmo && this.form.hmo.organization_name
                                   ? this.form.hmo.organization_name
                                   : "All",
            "Health Provider":   providerObj ? providerObj.agency_name : (this.form.provider_id || "All"),
            [this.label.place_of_work_label]: this.form.place_of_work || "All",
            Gender:              this.form.gender || "All",
            LGA:                 this.form.localgovt ? this.form.localgovt.local_name : "All",
            "Ward Name": this.form.ward ? this.form.ward.ward_name : "All",
            "Date From":         this.form.from || "All",
            "Date To":           this.form.to || "All",
            "Vulnerable Group":  this.form.category_of_vulnerable_group || "All",
            "Includes Dependents": this.form.dependents ? "Yes" : "No",
            "Grouped By":        this.form.group_by || "None",
          },
        };
      },
    },
    created() {
      this.getProviders();
      this.getMDAs();
      this.getTPAs();
    },
  };
</script>
